import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { ApiError, apiGet, apiSend } from "../../api";
import type { PortalConfig } from "../../config";
import type { Lookups, TicketDetail } from "../../types";

export interface TicketState {
  config: PortalConfig;
  lookups: Lookups | null;
  detail: TicketDetail;
  /** Sends a change; on success the page shows the ticket the server sends back. */
  save: (method: "PATCH" | "PUT" | "POST" | "DELETE", path: string, body?: unknown) => Promise<TicketDetail | null>;
  saving: boolean;
  reload: () => void;
  /** Shows a message at the top of the ticket (what an action did). */
  notify: (text: string | null) => void;
}

export const TicketContext = createContext<TicketState | null>(null);

export function useTicketContext(): TicketState {
  const t = useContext(TicketContext);
  if (!t) throw new Error("TicketContext missing");
  return t;
}

export interface Problem { text: string; conflict: boolean }

/** Loads one ticket and applies edits to it, one at a time. */
export function useTicket(config: PortalConfig, ticketNo: string) {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!token) return;
    const ctrl = new AbortController();
    setLoadError(null);
    apiGet<TicketDetail>(config, token, `/tickets/${encodeURIComponent(ticketNo)}`, ctrl.signal)
      .then((d) => { setDetail(d); setProblem(null); })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        setLoadError(e instanceof ApiError && e.status === 404 ? `Ticket #${ticketNo} was not found.` : String((e as Error).message ?? e));
      });
    return () => ctrl.abort();
  }, [config, token, ticketNo, version]);

  const save = useCallback(async (method: "PATCH" | "PUT" | "POST" | "DELETE", path: string, body?: unknown) => {
    if (!token) return null;
    setSaving(true);
    setProblem(null);
    setNotice(null);
    try {
      const d = await apiSend<TicketDetail>(config, token, method, `/tickets/${encodeURIComponent(ticketNo)}${path}`, body);
      setDetail(d);
      if (d.notice) setNotice(d.notice);
      return d;
    } catch (e) {
      const err = e as ApiError;
      setProblem({ text: err.message, conflict: err.status === 409 });
      return null;
    } finally {
      setSaving(false);
    }
  }, [config, token, ticketNo]);

  return { detail, loadError, problem, setProblem, notice, setNotice, saving, save, reload: () => setVersion((v) => v + 1) };
}
